import type { Color } from "../types/Color"

// Summed over R, G and B. Up to this a pixel is the background; up to three times it, it is the
// antialiased fringe between the background and the drawing, and is cut out partly.
const TOLERANCE = 48
const FRINGE = TOLERANCE * 3

// The whole pixels an image covers on the grid; around it is the transparent padding of the fit.
export interface Area {
  x: number
  y: number
  width: number
  height: number
}

const distance = (data: Uint8ClampedArray, offset: number, [r, g, b]: Color) =>
  Math.abs(data[offset] - r) + Math.abs(data[offset + 1] - g) + Math.abs(data[offset + 2] - b)

// The solid color a picked image sits on.
export class SourceBackground {
  // The opaque color at three or more of the image's corners. One corner may belong to the
  // drawing: an arrow's tip often sits on it.
  static find({ data, width }: ImageData, area: Area): Color | null {
    if (area.width < 1 || area.height < 1) return null

    const right = area.x + area.width - 1
    const bottom = area.y + area.height - 1
    const corners = [
      [area.x, area.y],
      [right, area.y],
      [area.x, bottom],
      [right, bottom],
    ]
      .map(([x, y]) => (y * width + x) * 4)
      .filter((offset) => data[offset + 3] === 255)

    for (const offset of corners) {
      const color: Color = [data[offset], data[offset + 1], data[offset + 2]]
      if (corners.filter((other) => distance(data, other, color) <= TOLERANCE).length >= 3) {
        return color
      }
    }
    return null
  }

  // A copy with the background cut out wherever it reaches the edge of the grid, across pixels
  // that are already transparent. The same color enclosed by the drawing stays.
  static cut({ data, width, height }: ImageData, color: Color): ImageData {
    const cut = new Uint8ClampedArray(data)
    const isQueued = new Uint8Array(width * height)
    const queue: number[] = []
    const enqueue = (x: number, y: number) => {
      if (x < 0 || y < 0 || x >= width || y >= height) return

      const index = y * width + x
      if (isQueued[index]) return

      isQueued[index] = 1
      queue.push(index)
    }

    for (let x = 0; x < width; x += 1) {
      enqueue(x, 0)
      enqueue(x, height - 1)
    }
    for (let y = 0; y < height; y += 1) {
      enqueue(0, y)
      enqueue(width - 1, y)
    }

    for (let index = queue.pop(); index !== undefined; index = queue.pop()) {
      const offset = index * 4
      const alpha = cut[offset + 3]
      if (alpha > 0) {
        const away = distance(cut, offset, color)
        // The fringe fades but stops the cut, so it can't leak through a soft outline.
        if (away > TOLERANCE) {
          if (away < FRINGE) {
            cut[offset + 3] = Math.round((alpha * (away - TOLERANCE)) / (FRINGE - TOLERANCE))
          }
          continue
        }
        cut[offset + 3] = 0
      }

      const x = index % width
      const y = (index - x) / width
      enqueue(x + 1, y)
      enqueue(x - 1, y)
      enqueue(x, y + 1)
      enqueue(x, y - 1)
    }
    return new ImageData(cut, width, height)
  }
}
